'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@repo/ui/lib/utils';
import {
  useWalletSession,
  type WalletSessionStatus
} from '@/hooks/use-wallet-session';
import { track } from '@/lib/analytics';
import { reportGlobalError } from '@/lib/observability';
import { WalletSessionPanel } from './wallet-session-panel';

/**
 * How long a session may sit in "loading" or "restoring" before the gate
 * stops waiting. This is a "stop waiting" threshold, not a retry interval:
 * 9s covers a slow but working load while still recovering a broken one
 * (an SDK that never loads, a Dynamic outage, a flaky network) well before
 * the user gives up on a silent spinner.
 */
const WALLET_SESSION_TIMEOUT_MS = 9_000;

type WalletGateLocation = 'wallet-gate' | 'overview';

/** A session with nobody signed in yet, or still finding out. */
export interface SignedOutSession {
  status: Extract<
    WalletSessionStatus,
    'loading' | 'connecting' | 'restoring' | 'disconnected'
  >;
  /** The wallet SDK never reported back within the timeout. */
  timedOut: boolean;
}

interface WalletGateProps {
  children: React.ReactNode;
  /** Title shown on the connect prompt. */
  title?: string;
  /** Description shown on the connect prompt. */
  description?: string;
  /** Extra classes for the gate container. */
  className?: string;
  /** Which gate this is, for analytics and error reports. */
  location?: WalletGateLocation;
  /**
   * False lets everything through. For a caller that gates one route and
   * renders the same gate on the others, so chrome passed as `shell` keeps
   * its place in the tree and is not remounted on navigation.
   */
  active?: boolean;
  /**
   * Replaces the gate while nobody is signed in. The Overview entry uses it
   * to show the full connect screen instead of a panel. A restore that has
   * stalled is not handed over: that visitor needs the reload panel.
   */
  signedOut?: (session: SignedOutSession) => React.ReactNode;
  /**
   * Chrome around the content and around the gate's own panel, and never
   * around `signedOut`. Lets the connect screen take the whole viewport
   * while every other state stays inside the app shell.
   */
  shell?: (content: React.ReactNode) => React.ReactNode;
}

/**
 * One timeout for the whole wait. It starts when the session begins loading
 * and is not restarted when loading turns into restoring, so the user gets
 * a recovery path after nine seconds in total. Nothing is reported while the
 * content is already on screen.
 */
function useWalletSessionTimeout(
  location: WalletGateLocation,
  waiting: boolean
) {
  const { status, sdkHasLoaded } = useWalletSession();
  const [timedOut, setTimedOut] = useState(false);
  const latest = useRef({ status, sdkHasLoaded });

  useEffect(() => {
    latest.current = { status, sdkHasLoaded };
  }, [sdkHasLoaded, status]);

  useEffect(() => {
    if (!waiting) {
      setTimedOut(false);
      return;
    }

    const timer = setTimeout(() => {
      setTimedOut(true);
      const sdkDidNotLoad =
        latest.current.status === 'loading' && !latest.current.sdkHasLoaded;
      const atOverview = location === 'overview';

      if (atOverview && sdkDidNotLoad) {
        track('onboarding.sdk_load_timed_out');
      } else {
        track('app.wallet_session_load_timed_out', { location });
      }

      void reportGlobalError(
        new Error(
          sdkDidNotLoad
            ? `Dynamic SDK did not report sdkHasLoaded within ${WALLET_SESSION_TIMEOUT_MS}ms`
            : `Wallet session did not finish restoring within ${WALLET_SESSION_TIMEOUT_MS}ms`
        ),
        atOverview
          ? {
              route: '/',
              feature: 'onboarding',
              timeoutMs: WALLET_SESSION_TIMEOUT_MS
            }
          : {
              route:
                typeof window === 'undefined'
                  ? 'unknown'
                  : window.location.pathname,
              feature: 'wallet-gate',
              action: 'restore-wallet-session',
              timeoutMs: WALLET_SESSION_TIMEOUT_MS
            }
      ).catch(() => {
        // Reporting must never replace the recovery path.
      });
    }, WALLET_SESSION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [location, waiting]);

  return timedOut;
}

function isSignedOut(
  status: WalletSessionStatus
): status is SignedOutSession['status'] {
  return (
    status === 'loading' ||
    status === 'connecting' ||
    status === 'restoring' ||
    status === 'disconnected'
  );
}

/**
 * Drop-in gate for authenticated, chain-scoped read surfaces. Dynamic's
 * primary wallet supplies the identity used for private requests. A locked or
 * disconnected browser wallet does not hide already-authorized reports;
 * transaction controls must separately require WalletSignerHealthProvider's
 * live signer state.
 *
 * Identity readiness is tested before anything else. "Connecting" and
 * "restoring" are also reported for someone who is already signed in (wagmi
 * reconnecting, a wallet being linked), and that must never take the page
 * away from them.
 *
 * Reuse this anywhere a section should be wallet-only — pass `title`/
 * `description` to tailor the copy.
 */
export function WalletGate({
  children,
  title = 'Connect your wallet',
  description = 'Your positions, performance, and transaction history live behind your wallet. Connect to unlock the full view. ROWA never takes custody of your funds.',
  className,
  location = 'wallet-gate',
  active = true,
  signedOut,
  shell = (content) => content
}: WalletGateProps) {
  const { status, isReady } = useWalletSession();
  const timedOut = useWalletSessionTimeout(
    location,
    active && !isReady && (status === 'loading' || status === 'restoring')
  );

  if (!active || isReady) return <>{shell(children)}</>;

  // `isReady` is the authority. A status of "ready" without it is not a
  // state the session should produce; if it ever does, fail closed and ask
  // for a connection instead of showing private content.
  const blockedStatus = status === 'ready' ? 'disconnected' : status;

  if (
    signedOut &&
    isSignedOut(blockedStatus) &&
    // A loaded SDK that cannot restore the session needs the reload panel.
    // An SDK that never loaded cannot vouch for any session, so the connect
    // screen is still the safe place to land.
    !(blockedStatus === 'restoring' && timedOut)
  ) {
    return <>{signedOut({ status: blockedStatus, timedOut })}</>;
  }

  return (
    <>
      {shell(
        <div
          className={cn(
            'flex min-h-[70vh] items-center justify-center px-6 py-16',
            className
          )}
        >
          <WalletSessionPanel
            status={blockedStatus}
            timedOut={timedOut}
            location={location}
            connectTitle={title}
            connectDescription={description}
          />
        </div>
      )}
    </>
  );
}
